import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Card, Typography, Empty, Select, Space } from 'antd';

const { Title, Text } = Typography;
const { Option } = Select;

const HoldingsHeatmap = ({ holdings }) => {
  const [viewType, setViewType] = React.useState('profitLoss'); // 'profitLoss' or 'weight'

  // 색상 결정 함수 - TradingView 스타일
  const getColor = (value, type) => {
    if (type === 'profitLoss') {
      // TradingView 스타일: 빨강(손실) - 회색(중립) - 초록(수익)
      if (value >= 10) return '#2E7D32';   // 짙은 초록
      if (value >= 5) return '#43A047';    // 진한 초록
      if (value >= 3) return '#66BB6A';    // 초록
      if (value >= 1) return '#81C784';    // 밝은 초록
      if (value >= 0) return '#C8E6C9';    // 연한 초록
      if (value >= -1) return '#FFCDD2';   // 연한 빨강
      if (value >= -3) return '#EF9A9A';   // 밝은 빨강
      if (value >= -5) return '#E57373';   // 빨강
      if (value >= -10) return '#EF5350';  // 진한 빨강
      return '#D32F2F'; // 매우 짙은 빨강
    } else {
      // 비중: 파랑 그라데이션 (TradingView 스타일)
      if (value >= 20) return '#1565C0'; // 짙은 파랑
      if (value >= 15) return '#1976D2'; // 진한 파랑
      if (value >= 10) return '#42A5F5'; // 파랑
      if (value >= 5) return '#90CAF9';  // 밝은 파랑
      return '#BBDEFB'; // 연한 파랑
    }
  };

  // Heatmap 데이터 준비
  const heatmapData = useMemo(() => {
    if (!holdings || holdings.length === 0) {
      return null;
    }

    // 총 시가총액 계산
    const totalMarketValue = holdings.reduce((sum, h) => {
      const mv = parseFloat(h.market_value) || 0;
      return sum + mv;
    }, 0);

    // console.log('Total Market Value:', totalMarketValue); // 디버깅
    // console.log('Holdings:', holdings); // 디버깅

    // 총 시가총액이 0이면 계산 불가
    if (totalMarketValue === 0) {
      return null;
    }

    // 수익률 또는 비중에 따라 정렬
    const sortedHoldings = [...holdings].sort((a, b) => {
      if (viewType === 'profitLoss') {
        return (b.profit_loss_rate || 0) - (a.profit_loss_rate || 0);
      } else {
        return (b.market_value || 0) - (a.market_value || 0);
      }
    });

    // treemap 형식으로 데이터 생성 (단일 시리즈)
    const treemapData = sortedHoldings.map(holding => {
      const marketValue = parseFloat(holding.market_value) || 0;
      const weight = totalMarketValue > 0 ? (marketValue / totalMarketValue * 100) : 0;
      const profitLossRate = parseFloat(holding.profit_loss_rate) || 0;
      const colorValue = viewType === 'profitLoss' ? profitLossRate : weight;
      
      // console.log(`${holding.stock_name} - MV: ${marketValue}, Weight: ${weight}%`); // 디버깅
      
      // 표시할 값을 미리 계산
      const displayValue = viewType === 'profitLoss' 
        ? `${profitLossRate >= 0 ? '+' : ''}${profitLossRate.toFixed(2)}%`
        : `${weight.toFixed(1)}%`;
      
      return {
        x: holding.stock_name,
        y: marketValue, // treemap은 y값으로 크기 결정
        fillColor: getColor(colorValue, viewType), // 색상 직접 할당
        symbol: holding.stock_symbol,
        quantity: holding.quantity || 0,
        avgPrice: holding.avg_buy_price || 0,
        currentPrice: holding.current_price || 0,
        marketValue: marketValue,
        profitLoss: profitLossRate,
        weight: weight,
        displayValue: displayValue // 표시할 값 저장
      };
    });

    return [{
      data: treemapData
    }];
  }, [holdings, viewType]);

  // ApexCharts 옵션
  const chartOptions = useMemo(() => {
    if (!heatmapData) return {};

    return {
      chart: {
        type: 'treemap',
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150
          },
        }
      },
      plotOptions: {
        treemap: {
          distributed: true,
          enableShades: false
        }
      },
      colors: viewType === 'profitLoss' 
        ? ['#D32F2F', '#EF5350', '#E57373', '#EF9A9A', '#FFCDD2', '#C8E6C9', '#81C784', '#66BB6A', '#43A047', '#2E7D32']
        : ['#BBDEFB', '#90CAF9', '#42A5F5', '#1976D2', '#1565C0'],
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          colors: ['#333']
        },
        formatter: function(text, op) {
          const dataPoint = op.w.config.series[op.seriesIndex].data[op.dataPointIndex];
          if (!dataPoint) return text;
          
          const symbol = dataPoint.symbol || '';
          const displayValue = dataPoint.displayValue || '';
          
          return [text, symbol, displayValue];
        }
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '12px',
      },
      tooltip: {
        enabled: true,
        custom: function({ seriesIndex, dataPointIndex, w }) {
          const data = w.config.series[seriesIndex].data[dataPointIndex];
          
          return `
            <div style="padding: 12px; min-width: 200px;">
              <div style="font-weight: bold; font-size: 14px; margin-bottom: 8px;">
                ${data.x} (${data.symbol})
              </div>
              <div style="font-size: 12px; line-height: 1.8;">
                <div>수량: ${data.quantity.toLocaleString()}주</div>
                <div>평균단가: ₩${data.avgPrice.toLocaleString()}</div>
                <div>현재가: ₩${data.currentPrice.toLocaleString()}</div>
                <div>평가금액: ₩${data.marketValue.toLocaleString()}</div>
                <div style="color: ${data.profitLoss >= 0 ? '#52c41a' : '#ff4d4f'}; font-weight: bold;">
                  수익률: ${data.profitLoss >= 0 ? '+' : ''}${data.profitLoss.toFixed(2)}%
                </div>
                <div>비중: ${data.weight.toFixed(2)}%</div>
              </div>
            </div>
          `;
        }
      }
    };
  }, [heatmapData, viewType]);

  if (!holdings || holdings.length === 0) {
    return (
      <Card bordered={false} className="criclebox h-full">
        <Title level={5}>보유 종목 히트맵</Title>
        <Empty 
          description="보유 중인 종목이 없습니다" 
          style={{ padding: '40px 0' }}
        />
      </Card>
    );
  }

  return (
    <Card bordered={false} className="criclebox h-full">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={5} style={{ margin: 0 }}>보유 종목 히트맵</Title>
          <Select
            value={viewType}
            onChange={setViewType}
            style={{ width: 150 }}
            size="small"
          >
            <Option value="profitLoss">수익률 기준</Option>
            <Option value="weight">비중 기준</Option>
          </Select>
        </div>
        
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {viewType === 'profitLoss' 
            ? '각 종목의 수익률을 색상으로 표시합니다 (빨강: 손실, 초록: 수익) - TradingView 스타일'
            : '각 종목의 포트폴리오 내 비중을 색상으로 표시합니다 (진한 파랑: 높은 비중)'
          }
        </Text>

        <div style={{ marginTop: 16 }}>
          {heatmapData && (
            <ReactApexChart
              options={chartOptions}
              series={heatmapData}
              type="treemap"
              height={900}
            />
          )}
        </div>
      </Space>
    </Card>
  );
};

export default HoldingsHeatmap;
